import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Upload, X as XIcon, Download, Copy, Check, ExternalLink } from 'lucide-react';
import api from '../../lib/api';

/**
 * Public waitlist page + QR code preview + logo upload, extracted from
 * the legacy SettingsPage.jsx. Owns its own logo cache-bust state.
 */
export default function QrSection({ practice, form, setF, t }) {
  const publicUrl = practice?.slug ? `${window.location.origin}/warteliste/${practice.slug}` : '';
  const qrPng = practice?.slug ? `${process.env.REACT_APP_BACKEND_URL}/api/qr/${practice.slug}.png` : '';
  const qrSvg = practice?.slug ? `${process.env.REACT_APP_BACKEND_URL}/api/qr/${practice.slug}.svg` : '';
  const [copied, setCopied] = useState(false);
  function copyLink() {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="so-card p-5" data-testid="qr-section">
      <h2 className="text-sm font-semibold mb-4">{t('settings.sections.publicPageQr')}</h2>
      <label className="flex items-start gap-3 mb-3">
        <input type="checkbox" className="mt-0.5" checked={!!form.public_enabled} onChange={(e) => setF('public_enabled', e.target.checked)} data-testid="set-public-enabled"/>
        <span className="text-sm">{t('settings.publicPage.enabled')}</span>
      </label>
      <div className="flex items-center gap-2 mb-3">
        <input className="so-input font-mono text-xs" readOnly value={publicUrl} data-testid="set-public-url"/>
        <button onClick={copyLink} type="button" className="so-btn so-btn-secondary" data-testid="set-copy-link">
          {copied ? <Check size={14}/> : <Copy size={14}/>}{copied ? t('settings.publicPage.copied') : t('settings.publicPage.copy')}
        </button>
        <a href={publicUrl} target="_blank" rel="noreferrer" className="so-btn so-btn-secondary"><ExternalLink size={14}/></a>
      </div>
      <div className="mt-3 flex items-center gap-4 flex-wrap">
        <QrPreview practice={practice} qrPng={qrPng} qrSvg={qrSvg} t={t}/>
      </div>
    </div>
  );
}

function QrPreview({ practice, qrPng, qrSvg, t }) {
  const [hasLogo, setHasLogo] = useState(!!practice?.qr_logo_data_url);
  const [busy, setBusy] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);
  const previewSrc = `${qrPng}${qrPng.includes('?') ? '&' : '?'}_=${cacheBust}`;

  async function onPick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t('settings.publicPage.logoMaxSize')); return; }
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      await api.post('/api/qr/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHasLogo(true); setCacheBust(Date.now());
      toast.success(t('settings.publicPage.logoAdded'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('settings.publicPage.uploadFailed'));
    } finally { setBusy(false); e.target.value = ''; }
  }
  async function onRemove() {
    setBusy(true);
    try {
      await api.delete('/api/qr/logo');
      setHasLogo(false); setCacheBust(Date.now());
      toast.success(t('settings.publicPage.logoRemoved'));
    } catch (_) { toast.error(t('settings.publicPage.removeFailed')); }
    finally { setBusy(false); }
  }
  function downloadWithAuth(url, filename) {
    const headers = { Authorization: `Bearer ${localStorage.getItem('studioops_access_token')}` };
    const pid = localStorage.getItem('studioops_active_practice');
    if (pid) headers['X-Practice-Id'] = pid;
    fetch(url, { headers })
      .then((r) => r.blob())
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = objUrl; a.download = filename;
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(objUrl); a.remove(); }, 500);
      })
      .catch(() => toast.error(t('settings.publicPage.downloadFailed')));
  }

  return (
    <>
      <div className="relative" data-testid="qr-preview-wrap">
        <img src={previewSrc} alt="QR" className="w-32 h-32 border border-border rounded bg-white" data-testid="set-qr-img"/>
        {hasLogo && (
          <button type="button" onClick={onRemove} disabled={busy}
            className="absolute -top-2 -right-2 bg-white border border-border rounded-full w-6 h-6 flex items-center justify-center shadow text-text-secondary hover:text-danger"
            aria-label={t('settings.publicPage.removeLogo')} data-testid="qr-logo-remove"><XIcon size={12}/></button>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <label className={`so-btn so-btn-secondary cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`} data-testid="qr-logo-upload-label">
          <Upload size={14}/>{hasLogo ? t('settings.publicPage.replaceLogo') : t('settings.publicPage.uploadLogo')}
          <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onPick} data-testid="qr-logo-input"/>
        </label>
        <button type="button" onClick={() => downloadWithAuth(qrPng, `qr-${practice.slug}.png`)} className="so-btn so-btn-secondary" data-testid="set-qr-png"><Download size={14}/>PNG</button>
        <button type="button" onClick={() => downloadWithAuth(qrSvg, `qr-${practice.slug}.svg`)} className="so-btn so-btn-secondary" data-testid="set-qr-svg"><Download size={14}/>SVG</button>
      </div>
      <p className="basis-full text-[11px] text-text-secondary mt-2" data-testid="qr-logo-dsgvo-note">{t('settings.publicPage.logoNote')}</p>
    </>
  );
}
